/**
 * ADR-029 — the pelvis owns its flesh. Resolve a family's pelvis-ownership
 * DECLARATION (ontology ids, a station, a seam fraction) against ONE rig:
 * which bone the thighs hang from (the owner), which rig bones are the spine
 * donors, where the station joint sits in bind-world, and the spine axis the
 * seam ramps along. Measures and names; moves no weight itself — the
 * adapter's re-own door applies it to a mesh.
 *
 * The disease this resolves (measured 2026-08-22, six Tripo bodies): the
 * generator's root stack (Hip → Pelvis → Waist) sits AT hip-joint height and
 * paints the buttock and crotch to `Waist` — the bone every retargeted clip
 * flexes for a bow. 30–50% of Waist's weight mass lies BELOW the hip joints;
 * `Pelvis` and `Hip` hold none. A bow therefore swings the buttock forward
 * about the hips against thighs that stay put: the flat butt on "bend at
 * waist" is an OWNERSHIP defect, the wrist-station class (ADR-025), not a
 * hinge defect (ADR-028). Flesh below the first lumbar station belongs to
 * the bone the thighs hang from.
 *
 * Two failure classes, deliberately different (ADR-028 §7's posture): a
 * declaration naming ontology ids the rig never mapped is a property of
 * the CHARACTER (a rig without a Spine1) and skips with a marker — the run
 * proceeds, the evidence says why the buttock still follows the spine; a
 * declaration whose OWNER is also a donor is a caller bug and refuses.
 *
 * Depends on: ir/rig-descriptor, ir/priors (the declaration shape), math/vec3.
 * Depended on by: the Document driver (run-document), the pelvis probe.
 */
import { KernelError } from '../ir/errors.js';
import type { RigBone, RigDescriptor } from '../ir/rig-descriptor.js';
import type { PelvisOwnership } from '../ir/priors.js';
import type { EvidenceMarker } from '../hands/separation-profile.js';
import { vec3Dot, vec3Length, vec3Scale, vec3Sub, type Vec3Data } from '../math/vec3.js';

export class PelvisOwnershipError extends KernelError {
  constructor(message: string) {
    super(`Cannot resolve pelvis ownership: ${message}`);
  }
}

/** The declaration made concrete against one rig — everything the re-own door needs. */
export interface ResolvedPelvisOwnership {
  /** The bone the thighs hang from — receives the re-owned weight. */
  readonly owner: string;
  /** Rig bones whose paint below the station is re-owned (spine chain, as mapped). */
  readonly donors: readonly string[];
  /** The station joint in bind-world — flesh below it (along the axis) is the pelvis's. */
  readonly stationWorld: Vec3Data;
  /** Unit axis up the spine from the station; the seam ramps along it. */
  readonly axisWorld: Vec3Data;
  /** Seam length above the station, world units (declared fraction × the station's own segment). */
  readonly seamWorld: number;
  /** The rig-side identity of the station, for evidence. */
  readonly stationBone: string;
  readonly provenance: readonly string[];
}

export interface ResolvePelvisOwnershipResult {
  /** Null when the rig's geometry or mapping cannot define the station — see markers. */
  readonly resolved: ResolvedPelvisOwnership | null;
  readonly markers: readonly EvidenceMarker[];
}

/** The rig bone mapped to an ontology id — the highest-confidence entry, or null. */
function mappedBone(rig: RigDescriptor, ontologyId: string): string | null {
  let best: { rigBone: string; confidence: number } | null = null;
  for (const m of rig.mapping) {
    if (m.ontologyId !== ontologyId) continue;
    if (best === null || m.confidence > best.confidence) best = { rigBone: m.rigBone, confidence: m.confidence };
  }
  return best?.rigBone ?? null;
}

export function resolvePelvisOwnership(rig: RigDescriptor, decl: PelvisOwnership): ResolvePelvisOwnershipResult {
  const markers: EvidenceMarker[] = [];
  const byName = new Map(rig.bones.map((b) => [b.name, b] as const));
  const skip = (reason: string): ResolvePelvisOwnershipResult => {
    markers.push({ code: 'pelvis-ownership-unresolved', detail: { family: decl.family, reason } });
    return { resolved: null, markers };
  };

  // --- the owner: the bone BOTH thighs hang from ---
  const leftThigh = mappedBone(rig, 'LeftUpLeg');
  const rightThigh = mappedBone(rig, 'RightUpLeg');
  if (leftThigh === null || rightThigh === null) {
    return skip(`rig maps no ${leftThigh === null ? 'LeftUpLeg' : 'RightUpLeg'} — no thighs to hang the pelvis from`);
  }
  const lParent = byName.get(leftThigh)?.parent ?? null;
  const rParent = byName.get(rightThigh)?.parent ?? null;
  if (lParent === null || rParent === null) {
    return skip(`a thigh ("${lParent === null ? leftThigh : rightThigh}") is a root — nothing hangs it`);
  }
  if (lParent !== rParent) {
    return skip(`the thighs hang from different bones ("${lParent}" vs "${rParent}") — no single pelvis`);
  }
  const owner = lParent;

  // --- the station and the donors, by ontology id ---
  const stationBone = mappedBone(rig, decl.station);
  if (stationBone === null) return skip(`rig maps no "${decl.station}" — no station to own below`);
  const donors = decl.donors.map((id) => mappedBone(rig, id)).filter((b): b is string => b !== null);
  if (donors.length === 0) return skip(`rig maps none of the donors [${decl.donors.join(', ')}]`);
  if (donors.includes(owner)) {
    // A declaration that names its own owner as a donor would move weight
    // onto itself — a caller bug, never a specimen property.
    throw new PelvisOwnershipError(
      `family "${decl.family}": owner "${owner}" (the thighs' parent) is also a donor — the declaration contradicts itself`,
    );
  }

  // --- the spine axis at the station: toward the station's donor child,
  //     else any child, else up from the owner (a leaf station) ---
  const station = byName.get(stationBone) as RigBone;
  const ownerBone = byName.get(owner) as RigBone;
  // The child that CONTINUES the spine: a mapped donor child first; else a
  // child aligned with the owner→station direction (a garment helper hung
  // under the station by a bone-union component would otherwise hijack the
  // axis — lateral, it fails the rise gate; diagonal, it tilts the seam —
  // Codex P2, #108); else the owner→station direction itself.
  const children = rig.bones.filter((b) => b.parent === stationBone);
  const up = vec3Sub(station.worldBind.position, ownerBone.worldBind.position);
  const upLen = vec3Length(up);
  const alignedWithSpine = (c: RigBone): boolean => {
    if (upLen < 1e-9) return false;
    const d = vec3Sub(c.worldBind.position, station.worldBind.position);
    const dLen = vec3Length(d);
    return dLen > 1e-9 && vec3Dot(d, up) / (dLen * upLen) > 0.5; // within 60° of the spine's rise
  };
  const along = children.find((c) => donors.includes(c.name)) ?? children.find(alignedWithSpine) ?? null;
  const segment = along !== null ? vec3Sub(along.worldBind.position, station.worldBind.position) : up;
  const segLen = vec3Length(segment);
  if (segLen < 1e-9) {
    markers.push({
      code: 'pelvis-station-degenerate',
      detail: { family: decl.family, station: stationBone, reason: 'the station segment has zero length — no spine axis' },
    });
    return { resolved: null, markers };
  }
  const axisWorld = vec3Scale(segment, 1 / segLen);
  // The station must sit ABOVE the owner along that axis — a station at or
  // below the pelvis would re-own the whole torso (every vertex reads
  // "below the station") and freeze the spine in a bow.
  const rise = vec3Dot(vec3Sub(station.worldBind.position, ownerBone.worldBind.position), axisWorld);
  if (!(rise > 1e-6)) {
    markers.push({
      code: 'pelvis-station-degenerate',
      detail: {
        family: decl.family,
        station: stationBone,
        reason: `station "${stationBone}" does not rise above owner "${owner}" along the spine (${rise.toFixed(4)})`,
      },
    });
    return { resolved: null, markers };
  }

  return {
    resolved: {
      owner,
      donors,
      stationWorld: station.worldBind.position,
      axisWorld,
      seamWorld: decl.seamFraction * segLen,
      stationBone,
      provenance: decl.provenance,
    },
    markers,
  };
}
